import { useState, useRef, useEffect } from "react";
import { Share2, Copy, Check, X } from "lucide-react";
import { WhatsAppIcon } from "./Logo";

const buildUrl = (slug) => `${window.location.origin}/api/s/${slug}`;
const buildText = (title) =>
  `Check out this property from Laxmi Jiwan Properties, Hisar:\n${title}`;

// Compact icon-only share — direct action (native share sheet or WhatsApp), no popover.
export function ShareIconButton({ slug, title, className = "" }) {
  const url = buildUrl(slug);
  const wa = `https://wa.me/?text=${encodeURIComponent(buildText(title) + "\n" + url)}`;

  const onClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (navigator.share) {
      try {
        await navigator.share({ title, text: buildText(title), url });
        return;
      } catch {
        /* user cancelled / fallthrough */
      }
    }
    window.open(wa, "_blank", "noopener,noreferrer");
  };

  return (
    <button
      onClick={onClick}
      aria-label="Share this property"
      data-testid={`share-icon-${slug}`}
      className={`inline-flex items-center justify-center rounded-full border border-[#131311] text-[#131311] hover:bg-[#c5a059] hover:border-[#c5a059] hover:text-white transition-all duration-300 ${className}`}
    >
      <Share2 className="h-4 w-4" />
    </button>
  );
}

// Full share control with a small aesthetic popover: WhatsApp + Copy link.
export function ShareMenu({ slug, title, label = "Share", wrapperClassName = "" }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const ref = useRef(null);
  const url = buildUrl(slug);
  const wa = `https://wa.me/?text=${encodeURIComponent(buildText(title) + "\n" + url)}`;

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const copy = async () => {
    try { await navigator.clipboard.writeText(url); } catch { /* noop */ }
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  return (
    <div className={`relative ${wrapperClassName}`} ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid={`share-menu-trigger-${slug}`}
        className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-[#131311] px-6 py-4 text-sm font-semibold text-[#131311] hover:bg-[#131311] hover:text-[#faf9f5] transition-all duration-300"
      >
        <Share2 className="h-4 w-4" /> {label}
      </button>

      {open && (
        <div
          className="absolute right-0 bottom-full mb-3 w-60 rounded-2xl bg-white border border-[#e7e3d7] shadow-[0_24px_60px_-20px_rgba(19,19,17,0.35)] overflow-hidden z-30"
          data-testid={`share-menu-${slug}`}
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#eee9dc]">
            <span className="text-xs font-semibold uppercase tracking-[0.18em] text-[#9a7b38]">Share property</span>
            <button onClick={() => setOpen(false)} className="text-[#8a8578] hover:text-[#131311]"><X className="h-4 w-4" /></button>
          </div>
          <a
            href={wa}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setOpen(false)}
            data-testid={`share-whatsapp-${slug}`}
            className="flex items-center gap-3 px-4 py-3 hover:bg-[#25d366]/10 transition-colors"
          >
            <WhatsAppIcon className="h-5 w-5 text-[#1eae53]" />
            <span className="text-sm font-semibold text-[#131311]">Share on WhatsApp</span>
          </a>
          <button
            onClick={copy}
            data-testid={`share-copy-${slug}`}
            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[#f4efdf] transition-colors"
          >
            {copied ? <Check className="h-5 w-5 text-[#1eae53]" /> : <Copy className="h-5 w-5 text-[#9a7b38]" />}
            <span className="text-sm font-semibold text-[#131311]">{copied ? "Link copied!" : "Copy link"}</span>
          </button>
        </div>
      )}
    </div>
  );
}
